/**
 * <gs-range> — a slider kept in step with the text field it sits under, both ways
 * (docs/gas-spring-finder.md §2.4). Markup: snippets/gas-spring-range.liquid.
 *
 * The field is the value: it submits, it is validated, and it is what the finder reads. The range
 * has no name, so it never submits. Dragging writes the field and fires input and change on it,
 * exactly as typing would, so whoever listens to the field cannot tell the two apart; typing, or a
 * script that fires input on the field (the Newton calculator does), moves the thumb. The thumb
 * follows the field's leading number, so a pasted "0500N" sits at 500.
 *
 * The span belongs to the owner: a `gs:span` event on the field sets it, detail
 * { min, max, step, note }. The finder sends one from its index whenever the chosen thread or the
 * index changes, and one with detail 0 after it has set the field from the URL, which only brings
 * the thumb back to the field. The server's span stands until then.
 */
(() => {
  if (customElements.get('gs-range')) return;

  class GsRange extends HTMLElement {
    connectedCallback() {
      const range = this.querySelector('input');
      const field = document.getElementById(this.dataset.for);
      const [lo, note, hi] = this.querySelector('[data-range-scale]').children;
      const unit = (n) => this.dataset.unit.replace('%N%', n);
      // One handler: the range's own input and change, and the field's input and gs:span.
      const on = (e) => {
        if (e.target === range) {
          if (e.type === 'input') field.value = range.value;
          return field.dispatchEvent(new Event(e.type, { bubbles: true }));
        }
        const d = e.detail;
        if (d) {
          for (const k of ['min', 'max', 'step']) range.setAttribute(k, d[k]);
          note.textContent = d.note;
        }
        // The field's leading number, spaces stripped as the finder reads it: "0500N" is 500,
        // "660.5" and "660,5" are 660.5, never 6605.
        const n = parseFloat(field.value.replace(/\s/g, '').replace(',', '.'));
        // The browser clamps the value to the span and snaps it to the step.
        range.value = n >= 0 ? n : range.min;
        const pct = ((range.value - range.min) / (range.max - range.min)) * 100 || 0;
        this.style.setProperty('--gs-fill', Math.min(100, Math.max(0, pct)) + '%');
        lo.textContent = unit(range.min);
        hi.textContent = unit(range.max);
      };
      const listen = (verb) => {
        for (const type of ['input', 'gs:span']) field[verb](type, on);
        for (const type of ['input', 'change']) this[verb](type, on);
      };
      listen('addEventListener');
      this.unmount = () => listen('removeEventListener');
      this.setAttribute('data-js', '');
      on({});
    }

    disconnectedCallback() {
      this.unmount();
    }
  }
  customElements.define('gs-range', GsRange);
})();
